import { useState, useEffect } from 'react'
import { Bot, Sparkles, Zap, CheckCircle, AlertCircle } from 'lucide-react'

interface AICoreData {
  status: 'active' | 'idle' | 'error'
  totalPrompts: number
  activePrompts: number
  requestsToday: number
  avgResponseTime: number
  lastRequest: string
}

export default function AICoreCard() {
  const [aiCore, setAiCore] = useState<AICoreData>({
    status: 'active',
    totalPrompts: 8,
    activePrompts: 7,
    requestsToday: 142,
    avgResponseTime: 1.2,
    lastRequest: new Date().toISOString()
  })

  useEffect(() => {
    // TODO: Implement real AI core monitoring
    const interval = setInterval(() => {
      setAiCore(prev => ({
        ...prev,
        requestsToday: prev.requestsToday + Math.floor(Math.random() * 3),
        lastRequest: new Date().toISOString()
      }))
    }, 60000)

    return () => clearInterval(interval)
  }, [])

  const getStatusColor = () => {
    switch (aiCore.status) {
      case 'active': return '#FF1493'
      case 'idle': return '#FFD93D'
      case 'error': return '#FF0000'
    }
  }

  return (
    <div style={{
      background: 'linear-gradient(135deg, #1A1F2E, #242938)',
      border: '2px solid #FF1493',
      borderRadius: '12px',
      padding: '24px',
      position: 'relative',
      overflow: 'hidden',
      boxShadow: '0 0 20px rgba(255, 20, 147, 0.2)',
      height: '100%'
    }}>
      {/* Corner accent */}
      <div style={{
        position: 'absolute',
        top: 0,
        right: 0,
        width: '100px',
        height: '100px',
        background: 'linear-gradient(135deg, transparent 50%, rgba(255, 20, 147, 0.2) 50%)',
        pointerEvents: 'none'
      }} />

      {/* Header */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        marginBottom: '20px'
      }}>
        <div style={{
          background: 'rgba(255, 20, 147, 0.2)',
          border: '2px solid #FF1493',
          borderRadius: '8px',
          padding: '10px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <Bot style={{ width: 24, height: 24, color: '#FF1493' }} />
        </div>
        <h3 style={{
          fontFamily: 'Rajdhani, sans-serif',
          fontSize: '20px',
          fontWeight: 700,
          color: '#FF1493',
          textTransform: 'uppercase',
          letterSpacing: '1px',
          margin: 0,
          textShadow: '0 0 10px rgba(255, 20, 147, 0.6)'
        }}>
          AI Core
        </h3>
      </div>

      {/* Status */}
      <div style={{
        background: `rgba(255, 20, 147, 0.1)`,
        border: `1px solid ${getStatusColor()}`,
        borderRadius: '8px',
        padding: '16px',
        marginBottom: '16px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        <div>
          <div style={{
            fontSize: '11px',
            color: '#8A95AA',
            letterSpacing: '1px',
            textTransform: 'uppercase',
            fontWeight: 600,
            marginBottom: '4px'
          }}>
            Neural Status
          </div>
          <div style={{
            fontSize: '24px',
            fontWeight: 700,
            color: getStatusColor(),
            fontFamily: 'Rajdhani, sans-serif',
            textTransform: 'uppercase'
          }}>
            {aiCore.status}
          </div>
        </div>
        {aiCore.status === 'active' ? (
          <Sparkles style={{ width: 24, height: 24, color: '#FF1493', animation: 'pulse 2s infinite' }} />
        ) : aiCore.status === 'idle' ? (
          <AlertCircle style={{ width: 24, height: 24, color: '#FFD93D' }} />
        ) : (
          <AlertCircle style={{ width: 24, height: 24, color: '#FF0000' }} />
        )}
      </div>

      {/* Metrics Grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '12px',
        marginBottom: '16px'
      }}>
        <div style={{
          background: 'rgba(255, 20, 147, 0.1)',
          border: '1px solid rgba(255, 20, 147, 0.3)',
          borderRadius: '6px',
          padding: '12px'
        }}>
          <div style={{
            fontSize: '11px',
            color: '#8A95AA',
            letterSpacing: '1px',
            textTransform: 'uppercase',
            fontWeight: 600,
            marginBottom: '8px'
          }}>
            Total Prompts
          </div>
          <div style={{
            fontSize: '20px',
            fontWeight: 700,
            color: '#FF1493',
            fontFamily: 'Rajdhani, sans-serif'
          }}>
            {aiCore.totalPrompts}
          </div>
        </div>

        <div style={{
          background: 'rgba(0, 240, 255, 0.1)',
          border: '1px solid rgba(0, 240, 255, 0.3)',
          borderRadius: '6px',
          padding: '12px'
        }}>
          <div style={{
            fontSize: '11px',
            color: '#8A95AA',
            letterSpacing: '1px',
            textTransform: 'uppercase',
            fontWeight: 600,
            marginBottom: '8px'
          }}>
            Active
          </div>
          <div style={{
            fontSize: '20px',
            fontWeight: 700,
            color: '#00F0FF',
            fontFamily: 'Rajdhani, sans-serif'
          }}>
            {aiCore.activePrompts}
          </div>
        </div>

        <div style={{
          background: 'rgba(107, 207, 127, 0.1)',
          border: '1px solid rgba(107, 207, 127, 0.3)',
          borderRadius: '6px',
          padding: '12px'
        }}>
          <div style={{
            fontSize: '11px',
            color: '#8A95AA',
            letterSpacing: '1px',
            textTransform: 'uppercase',
            fontWeight: 600,
            marginBottom: '8px'
          }}>
            Requests Today
          </div>
          <div style={{
            fontSize: '20px',
            fontWeight: 700,
            color: '#6BCF7F',
            fontFamily: 'Rajdhani, sans-serif'
          }}>
            {aiCore.requestsToday}
          </div>
        </div>

        <div style={{
          background: 'rgba(255, 217, 61, 0.1)',
          border: '1px solid rgba(255, 217, 61, 0.3)',
          borderRadius: '6px',
          padding: '12px'
        }}>
          <div style={{
            fontSize: '11px',
            color: '#8A95AA',
            letterSpacing: '1px',
            textTransform: 'uppercase',
            fontWeight: 600,
            marginBottom: '8px'
          }}>
            Avg Response
          </div>
          <div style={{
            fontSize: '20px',
            fontWeight: 700,
            color: '#FFD93D',
            fontFamily: 'Rajdhani, sans-serif'
          }}>
            {aiCore.avgResponseTime}s
          </div>
        </div>
      </div>

      {/* Action Button */}
      <button style={{
        width: '100%',
        padding: '12px',
        background: 'linear-gradient(135deg, #FF1493, #C71585)',
        border: '2px solid #FF1493',
        borderRadius: '6px',
        color: '#E0E6F0',
        cursor: 'pointer',
        fontWeight: 700,
        fontSize: '14px',
        textTransform: 'uppercase',
        fontFamily: 'Rajdhani, sans-serif',
        boxShadow: '0 0 20px rgba(255, 20, 147, 0.4)',
        transition: 'all 0.2s',
        marginBottom: '12px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '8px'
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.boxShadow = '0 0 30px rgba(255, 20, 147, 0.6)'
        e.currentTarget.style.transform = 'translateY(-2px)'
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.boxShadow = '0 0 20px rgba(255, 20, 147, 0.4)'
        e.currentTarget.style.transform = 'translateY(0)'
      }}
      onClick={() => window.location.href = '/admin/ai-monitoring'}
      >
        <Zap style={{ width: 16, height: 16 }} />
        View AI Monitoring
      </button>

      {/* Last Request */}
      <div style={{
        fontSize: '11px',
        color: '#8A95AA',
        textAlign: 'center'
      }}>
        Last Request: {new Date(aiCore.lastRequest).toLocaleString()}
      </div>
    </div>
  )
}
